import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Field, ErrorMessage } from 'formik';
import * as Icons from 'react-feather';

import './css/basicTopicDetails.css';

import ImageDnD from '../common/form/imageDnD';
import { Toggle } from '../common/form/toggle';
import BSFormControl from '../common/form/bsFormControl';
import ServerErrorMessage from '../common/serverErrorMessage';

import Languages from '../../helpers/languages';
import { applyImageCropAndWatermark, getTopicPreviewUrl, getValueByFieldPath } from '../../helpers/utils';
import logger from '../../helpers/logger';
import FeaturesContext from '../../scenes/contexts/featuresContext';
import { ENTITY_FEATURES } from 'hollerlive-shared/constants';
import { COMPARATORS } from '../../helpers/featuresHelper';
import NoPermissionLabel from '../common/noPermissionLabel';
import messages from '../../helpers/messages';
import PermissionsContext from '../../scenes/contexts/permissionsContext';
import { PERMISSIONS } from '../../helpers/permissionsHelper';
import DebouncedInputField from '../common/form/debouncedInputField';

export class BasicTopicDetails extends Component {
    constructor(props) {
        super(props);
        const { basicSettings } = props.values;

        let imgPreview = "";
        let urlPreview = "";

        if (basicSettings) {
            imgPreview = basicSettings.processedImage ? basicSettings.processedImage : basicSettings.imageUrl;
            urlPreview = basicSettings.title ? getTopicPreviewUrl(basicSettings.title) : "";
        }

        this.state = {
            displayNameWarning: '',
            urlPreview,
            imgPreview
        };

        this.handleFieldValueChange = this.handleFieldValueChange.bind(this);
        this.handleFieldTouchedChange = this.handleFieldValueChange.bind(this);
        this.handlePrivateSet = this.handlePrivateSet.bind(this);
        this.validateDuplicateTopicName = this.validateDuplicateTopicName.bind(this);
    }

    static getDerivedStateFromProps(props) {
        const { title } = props.values.basicSettings;
        
        if (title && title.length > 0) {
            return {
                urlPreview: getTopicPreviewUrl(title)
            };
        }

        return null;
    }

    getLangByCode(code) {
        return Languages.find(l => l.code === code);
    }

    isTopicDisplayNameTooLong(value) {
        return value ? value.length > 80 : false;
    }

    handleImageUpload(imageObj, setFieldValue) {
        applyImageCropAndWatermark(imageObj).then(processedImg => {
            setFieldValue('basicSettings.processedImage', processedImg);
            this.setState({ imgPreview: processedImg });
        }).catch(err => {
            logger.error(err);
        });
    }

    getLanguagesOptions() {
        return Languages.map(l => {
            return (
                <option key={l.code} value={l.code}>
                    {l.name}
                </option>
            );
        });
    }

    onImageError(setFieldError, reason) {
        setFieldError('basicSettings.processedImage', reason);
    }

    fieldHasErrors(errors = {}, field){
        const fieldError = getValueByFieldPath(errors, field);
        return fieldError && fieldError.length > 0;
    }

    handleFieldValueChange(field, value) {
        this.props.setFieldValue(field, value);
    }

    validateDuplicateTopicName(topicName) {
        this.context.validateTopicNameDuplicate(topicName);
    }

    handlePrivateSet(value) {
        if(value) {
            this.props.setFieldValue('postVoteSettings.showCopyLink', false);
            this.props.setFieldValue('postVoteSettings.showFbShare', false);
        } else {
            this.props.setFieldValue('postVoteSettings.showCopyLink', this.props.model.postVoteSettings.showCopyLink);
            this.props.setFieldValue('postVoteSettings.showFbShare', this.props.model.postVoteSettings.showFbShare);
        }
        this.props.setFieldValue('basicSettings.isPrivate', value);
        this.props.setFieldTouched('basicSettings.isPrivate');
    }
    
    clearImage() {
        this.handleFieldValueChange('basicSettings.imageUrl', '');
        this.handleFieldValueChange('basicSettings.processedImage', null);
        this.setState({ imgPreview: null });
    }

    render() {
        const { 
            editMode,
            backendErrors, 
            errors, 
            values, 
            setFieldValue, 
            setFieldError,
            handleBlur,
            isDirty 
        } = this.props;

        return (
            <div className='basic-topic-details'>
                <div className="row-fluid clearfix uitest-topic-visibility">
                    <div className="col-md-12 row-fluid">
                        <div className="col-md-6">
                            <FeaturesContext
                                requiredFeatures={[{
                                    name: ENTITY_FEATURES.HAS_PRIVATE_TOPICS,
                                    compare: COMPARATORS.IS_TRUE
                                }]}
                                renderOtherwise={
                                    <BSFormControl 
                                        className='row-fluid private-field-containter' inline={true}
                                        hasErrors={this.fieldHasErrors(errors, 'basicSettings.isPrivate')}
                                    >
                                        <Field name='basicSettings.isPrivate'>
                                            {({ field }) =>
                                                <div>
                                                    <NoPermissionLabel
                                                        position='left'
                                                        message={messages.permissions.features.noPrivateTopics}
                                                    />
                                                    <Toggle value={field.value} disabled={true} />
                                                    <label>private topic</label>
                                                    <label className='sub-label'>
                                                        private topics do not show in the holler.live search results and
                                                        cannot be shared
                                                    </label>
                                                </div>
                                            }
                                        </Field>
                                    </BSFormControl>
                                }
                                >
                                <BSFormControl 
                                    className='row-fluid private-field-containter' inline={true}
                                    hasErrors={this.fieldHasErrors(errors, 'basicSettings.isPrivate')}
                                >
                                    <Field name='basicSettings.isPrivate'>
                                        {({ field }) =>
                                            <div>
                                                <Toggle value={field.value} onChange={(value) => this.handlePrivateSet(value)} />
                                                <label>private topic</label>
                                                <label className='sub-label'>private topics do not show in the holler.live search
                                                    results and cannot be shared</label>
                                            </div>
                                        }
                                    </Field>
                                    <ErrorMessage name="basicSettings.isPrivate" component="span" className='control-label'/>
                                </BSFormControl>
                            </FeaturesContext>
                        </div>

                        <div className="col-md-6">
                            <PermissionsContext requiredPermissions={[PERMISSIONS.ADMIN]}
                                renderOtherwise={
                                    <BSFormControl
                                        className='row-fluid disabled-field-containter' inline={true}
                                        hasErrors={this.fieldHasErrors(errors, 'basicSettings.isDisabled')}
                                    >
                                        <Field name='basicSettings.isDisabled'>
                                            {({ field }) =>
                                                <div>
                                                    <Toggle value={field.value} disabled={true} />
                                                    <label>disable topic</label>
                                                    <label className='sub-label'>disabled topics cannot be voted on</label>
                                                </div>
                                            }
                                        </Field>
                                        <ErrorMessage name="basicSettings.isDisabled" component="span" className='control-label'/>
                                    </BSFormControl>
                                }>
                                <BSFormControl 
                                    className='row-fluid disabled-field-containter' inline={true}
                                    hasErrors={this.fieldHasErrors(errors, 'basicSettings.isDisabled')}
                                >
                                    <Field name='basicSettings.isDisabled'>
                                        {({ field }) =>
                                            <div>
                                                <Toggle value={field.value} onChange={(value) => this.handleFieldValueChange(field.name, value)} />
                                                <label>disable topic</label>
                                                <label className='sub-label'>disabled topics cannot be voted on</label>
                                            </div>
                                        }
                                    </Field>
                                    <ErrorMessage name="basicSettings.isDisabled" component="span" className='control-label'/>
                                </BSFormControl>
                            </PermissionsContext>
                        </div>
                    </div>
                </div>

                <div className="row-fluid clearfix uitest-topic-default-lang">
                    <div className="col-md-12">
                        <BSFormControl label="default language">
                            <div className="col-md-4">
                                <Field disabled={editMode} component="select" className="form-control" name="basicSettings.defaultLang">
                                    {this.getLanguagesOptions()}
                                </Field>
                                <ErrorMessage name="basicSettings.defaultLang" component="span" className='control-label' />
                                {isDirty === false && 
                                    <ServerErrorMessage errors={backendErrors} name="basicSettings.defaultLang" className="control-label" />
                                }
                            </div>
                            <div className="col-md-6">
                                {!editMode && (
                                    <p className="message bg-warning">
                                        <Icons.AlertTriangle size={14} /> The Default Language cannot be changed once the topic is
                                        published.
                                    </p>
                                )}
                            </div>
                        </BSFormControl>
                    </div>
                </div>

                <div className="row-fluid uitest-topic-name single-line-inputs clearfix">
                    <BSFormControl 
                        label={'topic name'} 
                        subLabel={'topic alias name, used as identifier'}
                        hasErrors={this.fieldHasErrors(errors, 'basicSettings.title')}
                        required
                    >
                        {editMode ? (
                            <Field type="text" name="basicSettings.title" className="form-control" disabled={true} />
                        ) :
                            <Field name="basicSettings.title">
                                {({ field }) => (
                                    <DebouncedInputField 
                                        onDebouncedAction={this.validateDuplicateTopicName}
                                        value={field.value}
                                        debounceOnChangeTimeout={1500}
                                        placeholder="My New Topic Name" 
                                        onValueChange={(topicName) => this.handleFieldValueChange(field.name, topicName)}
                                        onBlur={handleBlur}
                                        onFocus={handleBlur}
                                        name={field.name}
                                    />
                                )}
                            </Field>
                        }
                        <ErrorMessage name="basicSettings.title" component="span" className='control-label' />  
                        <ServerErrorMessage errors={backendErrors} name="basicSettings.title" className="control-label" /> 

                        {!editMode && (
                            <p className="message bg-warning">
                                <Icons.AlertTriangle size={14} /> The Topic Title cannot be changed once the topic is
                                published.
                            </p>
                        )}
                    </BSFormControl>

                    <BSFormControl label={'topic url name'} subLabel={'topic url preview'}>
                        <input type="text" name="urlPreview" className="form-control" placeholder="holler.live/#" value={this.state.urlPreview} disabled />
                    </BSFormControl>
                </div>

                <div className="row-fluid clearfix">
                    <BSFormControl label={'topic display name'} subLabel={'how do you feel about'} hasErrors={this.fieldHasErrors(errors, 'basicSettings.displayName')}>
                        <Field type="text" name="basicSettings.displayName" className="form-control" placeholder={values.basicSettings && values.basicSettings.title} />
                        <ErrorMessage name="basicSettings.displayName" component="span" className='control-label' />
                        {this.isTopicDisplayNameTooLong(values.basicSettings.displayName) && (
                            <p className="message bg-warning">
                                <Icons.AlertTriangle size={14} /> Recommended length of topic display name is max 80 characters
                            </p>
                        )}
                    </BSFormControl>
                </div>

                <BSFormControl label={'topic description'} className="clearfix row-fluid">
                    <Field component="textarea" name="basicSettings.description" className="form-control" />
                </BSFormControl>
                
                <BSFormControl
                    label={'External description url'}
                    subLabel={'Add an external website as additional information if needed'}
                >
                    <Field type="text" name="basicSettings.extDescriptionUrl" className="form-control" />
                </BSFormControl>
                
                <FeaturesContext 
                    requiredFeatures={[{name: ENTITY_FEATURES.CUSTOM_TOPIC_IMAGE, compare:COMPARATORS.IS_TRUE}]}
                    renderOtherwise={
                        <BSFormControl inline={true}>
                            <div className="images-container">
                                <div className="image-dnd-container">
                                    <NoPermissionLabel position="left" message={messages.permissions.features.noCustomTopicImage} />
                                    <ImageDnD
                                        onDrop={() => {}}
                                        image={''}
                                        onRejectedFile={() => {}}
                                        onClear={() => {}}
                                        showClearButton={false}
                                        className={'imageWrapper'}
                                        disabled={true} 
                                    />
                                </div>
                            </div>
                        </BSFormControl>
                    }
                >
                    <BSFormControl hasErrors={this.fieldHasErrors(errors, 'basicSettings.processedImage')}>
                        <Field name="basicSettings.processedImage">
                            {({ form }) =>
                                <div className="form-group">
                                    <div className="images-container">
                                        <div className="image-dnd-container">
                                            <ImageDnD
                                                onDrop={(file) => this.handleImageUpload(file, setFieldValue)}
                                                image={this.state.imgPreview}
                                                onRejectedFile={(reason) => this.onImageError(setFieldError, reason)}
                                                onClear={() => this.clearImage()}
                                                showClearButton={true}
                                                className={'imageWrapper' + (this.fieldHasErrors(errors, 'basicSettings.processedImage') ? ' imageWrapperError' : '')}
                                            />
                                        </div>
                                        <div className="image-preview">
                                            <div className="imageWrapper">
                                                <img src={this.state.imgPreview} alt=""/>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        {form.errors && form.errors.basicSettings && form.errors.basicSettings.processedImage &&
                                        <span
                                            className='control-label'>{form.errors.basicSettings.processedImage}</span>
                                        }
                                        <ServerErrorMessage errors={backendErrors} name="basicSettings.processedImage"
                                                            className="control-label"/>
                                    </div>
                                </div>
                            }
                        </Field>
                    </BSFormControl>
                </FeaturesContext>
                {/* nickey: 09.07.18 Coment out for now, as those options are not used, but we want to keep the model fields. */}
                {/* <div className="form-group">
                        <Control.checkbox model=".voterCanSuggestPic"/>
                        <span className="checkbox-label">Votes can suggest different topic picture after voting</span>
                    </div>
                    <div className="form-group">
                        <Control.checkbox model=".voterCanAddComment"/>
                        <span className="checkbox-label">Voter can add comments after voting</span>
                    </div> */}
                <div className="form-group">
                    <Field name="basicSettings.voterShouldConcent">
                        {({ field }) => 
                            <Toggle value={field.value} onChange={(value) => this.handleFieldValueChange(field.name, value)} />
                        } 
                    </Field>
                    <span className="checkbox-label">Voter should give consent for contacting them</span>
                </div>
            </div>
        );
    }
}

BasicTopicDetails.propTypes = {
    values: PropTypes.shape({
        basicSettings: PropTypes.shape({
            id: PropTypes.string,
            title: PropTypes.string,
            displayName: PropTypes.string,
            defaultLang: PropTypes.string,
            description: PropTypes.string,
            extDescriptionUrl: PropTypes.string,
            imageUrl: PropTypes.string,
            voterShouldConcent: PropTypes.bool,
            isPrivate: PropTypes.bool
        })
    }).isRequired,
    model: PropTypes.shape(),
    backendErrors: PropTypes.shape({
        basicSettings: PropTypes.object
    }),
    errors: PropTypes.object,
    editMode: PropTypes.bool,
    setFieldValue: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired,
    setFieldError: PropTypes.func.isRequired,
    handleBlur: PropTypes.func.isRequired,
    isDirty: PropTypes.bool,
};

BasicTopicDetails.contextTypes = {
	validateTopicNameDuplicate: PropTypes.func
};

export default BasicTopicDetails;
